import api from "@/api/axios";

const API_URL = "/prompt-versions";

/**
 * Prompt-Versionen, neueste zuerst.
 *
 * promptKey ist optional ("llm_prompt_template" | "rag_prompt_template").
 * Ohne Angabe kommen beide Schlüssel.
 */
export const listPromptVersions = (promptKey = null) => {
    const params = {};
    if (promptKey) params.prompt_key = promptKey;
    return api.get(API_URL, { params });
};

// ACHTUNG, hier stand bis zum 20.08. getPromptVersion(versionId). Der
// Export hatte im ganzen Baum keinen einzigen Konsumenten -- weder in
// frontend/src noch in backend/tests. Der zugehoerige Endpunkt
// GET /prompt-versions/{id} (routers/prompt_versions.py) BLEIBT: er kann
// von aussen gerufen werden, und ein Endpunkt zu entfernen, weil die
// eigene Oberflaeche ihn nicht braucht, waere eine Annahme ueber fremde
// Aufrufer.

/**
 * Welcher der beiden Schlüssel gerade bewertet — abgeleitet aus
 * `system_settings.async_rating_mode`, nicht geraten.
 *
 * ⚠️ Der Liste ist das nicht anzusehen: sie führt beide Schlüssel
 * nebeneinander, und eine Version des *anderen* lässt sich vergleichen,
 * freigeben und aktivieren — alles läuft durch, und die Bewertung ändert sich
 * um kein Zeichen. Genau diese Auskunft fehlte an beiden Bedienstellen.
 *
 * `prompt_key` ist `null` im Modus **ML**: dort bewertet ein Modell ohne
 * Prompt. Das ist kein Mangel und darf nicht wie einer aussehen — keine
 * Version wirkt dann, und das ist die richtige Antwort, nicht eine fehlende.
 */
export const getEffectivePromptKey = () => api.get(`${API_URL}/effective-key`);

/**
 * Eine Version aktiv schalten.
 *
 * Antwortet mit 409, wenn das Gate blockiert — der Grund steht im Klartext in
 * `detail`. Diese Antwort ist kein Fehler der Oberfläche, sondern die Auskunft,
 * warum die Version noch nicht freigegeben ist.
 */
export const activatePromptVersion = (versionId) =>
    api.post(`${API_URL}/${versionId}/activate`);

/**
 * Gegen welchen Satz gemessen *würde* — und was dort zu messen ist (R7.10).
 *
 * Die Auskunft, die vor dem Start fehlte. Bis hierher nannte die Seite den
 * Vergleichssatz erst in der **Antwort** auf `compare`, also nach dem Start:
 * wer eine von Hand angelegte Version gegen `default` maß, erfuhr nach sechs
 * Minuten Laufzeit, dass es dort nichts zu messen gab (`n_human_cells: 0`,
 * drei Kennzahlen auf `null`, `verdict()` trotzdem `passed`).
 *
 * ⚠️ `sets_error` ist NICHT dasselbe wie `sets: []`. Das erste heißt „die
 * Zählung war nicht zu bekommen", das zweite „es gibt keine Sätze". Wer beides
 * gleich behandelt, zeigt die Warnung „null Referenzzellen" ausgerechnet dann,
 * wenn das Bewertungs-Backend nicht antwortet — eine Aussage über die Daten,
 * die in Wahrheit eine über das Netz ist.
 *
 * `default_set_reason` ist ein Schlüssel (`"reflektion"` | `"standard"`), kein
 * Satz. Formuliert wird hier.
 */
export const getComparePreview = (versionId) =>
    api.get(`${API_URL}/${versionId}/compare-preview`);

/**
 * Den Regressionsvergleich starten (R6).
 *
 * Antwortet sofort mit 202 — der Lauf fährt ZWEI vollständige Bewertungen
 * (Baseline und Kandidat) und dauert entsprechend. Der Fortschritt steht
 * danach in `gate_status`; die Seite fragt ihn per Reload nach.
 *
 * 409 heißt: der Zustand lässt den Vergleich nicht zu (läuft schon, keine
 * aktive Version, bereits aktiv). 502 heißt: die beiden Dienste sind nicht
 * richtig miteinander verbunden — das ist kein Anmeldeproblem.
 *
 * ⚠️ `setName` ist ohne Angabe `null` und NICHT "default". Der Unterschied ist
 * der Kern von R7.8: `null` heißt „das Backend entscheidet anhand der Herkunft
 * der Version" — stammt sie aus einem Reflektions-Vorschlag, wird gegen den
 * Satz gemessen, aus dem der Vorschlag entstanden ist. Stünde hier weiter
 * "default", liefe jeder Reflektions-Entwurf gegen einen Satz OHNE menschliche
 * Korrekturen, und die vier Kennzahlen aus R7.0 kämen leer zurück
 * (`n_human_cells: 0`) — ununterscheidbar von „es gibt nichts zu holen".
 *
 * Welcher Satz es geworden ist, steht in der Antwort (`set_name`). Die Seite
 * schreibt ihn hin, statt ihn zu vermuten.
 *
 * Seit R7.10 schickt die Versionsseite den Satz **ausdrücklich** mit — auch
 * die unveränderte Vorgabe, die sie sich vorher über `getComparePreview` aus
 * derselben Funktion geholt hat. Der Dialog sagt vor dem Start zu, gegen
 * welchen Satz gemessen wird; leitete das Backend danach erneut ab, könnte
 * inzwischen ein Vorschlag dazugekommen sein und der Lauf liefe gegen einen
 * anderen als den zugesagten. `null` bleibt der Weg für alles, was den Satz
 * nicht kennt (Kommandozeile, ausgefallene Vorschau).
 */
export const comparePromptVersion = (versionId, setName = null) =>
    api.post(`${API_URL}/${versionId}/compare`, { set_name: setName });

/**
 * Ein hängendes `gate_status='running'` zurücksetzen.
 *
 * Gebraucht nur, wenn ein Vergleich mit seinem Prozess gestorben ist. Läuft er
 * noch, lehnt das Backend ab — diese Prüfung liegt dort, weil nur der Dienst,
 * der den Lauf gestartet hat, weiß, ob er noch läuft.
 */
export const releasePromptVersion = (versionId) =>
    api.post(`${API_URL}/${versionId}/release`);

/**
 * Einen LAUFENDEN Vergleich anhalten.
 *
 * Das Gegenstück zu `release`, nicht dasselbe: `release` setzt ein hängendes
 * `running` zurück, das keinen Lauf mehr hinter sich hat; `cancel` hält einen
 * an, der noch läuft. Welcher Fall vorliegt, weiß nur das Backend — deshalb
 * zeigt die Seite beide Knöpfe, und jede Absage nennt den anderen Weg.
 *
 * Antwortet mit 202: das quittiert die **Annahme**, nicht das Ende. Der Lauf
 * beendet die gerade laufende Nachricht noch (bis zu `RATING_API_TIMEOUT`,
 * 90 s) und setzt die Version danach auf `draft`. Die Seite pollt weiter und
 * zeigt den Übergang von selbst.
 */
export const cancelPromptVersion = (versionId) =>
    api.post(`${API_URL}/${versionId}/cancel`);
